/**
 * The capability ladder — one row per stop, plus the fixed asks. The row
 * count equals the number of decisions: five rows explain four choices.
 *
 * Each stop row carries a mark, the stop's name, a one-line summary of what
 * it adds, a hint where one carries information, and a disclosure to the
 * stop's individual capabilities (where per-scope partials like "Deals only"
 * are named). The mark shows the TRUTH across the stop's capabilities, not
 * the level: all granted, some (with an "N of M" count), or none.
 *
 * The marks are controls. A stop's mark sets the level cumulatively — press
 * an unchecked or partial stop and it becomes the level; press a checked
 * stop and the level drops to the one below — so the ladder is a vertical
 * twin of the stepper and the two can never disagree. Inside a stop, a
 * capability's mark turns that capability on or off across every scope it
 * applies to. Basics is always on.
 *
 * With `previewGrants` (a stop hovered on the stepper), the marks that would
 * change are GHOSTED — never flipped to their future state and back, which
 * reads as a flash. The marks themselves do not preview on hover: hovering a
 * checkbox shows a hover ring, and its state changes once, on press.
 */

import { SlashCircle01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import {
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import type { LevelCheckState } from '@/lib/base/forms/level-check';

import {
  ALWAYS_ASKS_FOOTER,
  ALWAYS_ASKS_INTRO,
  ALWAYS_ASKS_ITEMS,
  ALWAYS_ASKS_ROW_LABEL,
  ALWAYS_ASKS_ROW_SUMMARY,
  CAPABILITY_LABELS,
  CONTACTS_SCOPE_LABEL,
  describePartialCoverage,
  GROUP_HINTS,
  STOP_LABELS,
  STOP_SUMMARIES,
} from '../agent-autonomy-copy';
import type {
  AutonomyGrantState,
  AutonomyStop,
  CapabilityCoverage,
  CapabilityKey,
  CapabilityState,
} from '../autonomyPresets';
import {
  AUTONOMY_STOPS,
  CAPABILITY_KEYS,
  CAPABILITY_STOPS,
  deriveCapabilityCoverage,
  isAutonomyStop,
} from '../autonomyPresets';
import type { AgentCaseType } from '../hooks/useAgentSettings';
import { DisclosureRow, LeafRow, ROW_INDENT, RowChevron } from './AutonomyRows';

const MARK_BY_STATE: Record<CapabilityState, LevelCheckState> = {
  all: 'checked',
  some: 'mixed',
  none: 'unchecked',
  'n/a': 'unchecked',
};

// ---------------------------------------------------------------------------
// Per-stop roll-up
// ---------------------------------------------------------------------------

interface StopCoverage {
  state: CapabilityState;
  onCount: number;
  total: number;
}

function rollUp(
  stop: AutonomyStop,
  coverage: Record<CapabilityKey, CapabilityCoverage>
): StopCoverage {
  const keys = CAPABILITY_KEYS.filter(
    key => CAPABILITY_STOPS[key] === stop && coverage[key].state !== 'n/a'
  );
  const total = keys.length;
  if (total === 0) {
    return { state: 'n/a', onCount: 0, total: 0 };
  }
  const onCount = keys.filter(key => coverage[key].state === 'all').length;
  const anyPartial = keys.some(key => coverage[key].state === 'some');
  if (onCount === total) {
    return { state: 'all', onCount, total };
  }
  if (onCount > 0 || anyPartial) {
    return { state: 'some', onCount, total };
  }
  return { state: 'none', onCount, total };
}

/** The level a press on a stop's mark asks for: the stop itself, or — when
 *  the stop is already fully on — the one below it. */
function levelAfterPress(stop: AutonomyStop, fullyOn: boolean): AutonomyStop {
  const target = fullyOn ? stop - 1 : stop;
  return isAutonomyStop(target) ? target : 0;
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

interface CapabilityRowProps {
  capability: CapabilityKey;
  coverage: CapabilityCoverage;
  preview: CapabilityCoverage | null;
  isDisabled: boolean;
  onCapability(key: CapabilityKey, on: boolean): void;
}

/** One capability inside a stop's expanded panel. */
function CapabilityRow({
  capability,
  coverage,
  preview,
  isDisabled,
  onCapability,
}: CapabilityRowProps) {
  // A preview ghosts the mark; the text stays put (changing text flickers).
  const previewed =
    preview !== null && preview.state !== coverage.state ? preview.state : null;
  const handleMark = useCallback(
    (on: boolean) => onCapability(capability, on),
    [onCapability, capability]
  );
  return (
    <li data-state={coverage.state} data-preview={previewed ?? undefined}>
      <LeafRow
        mark={MARK_BY_STATE[coverage.state]}
        markPreview={previewed === null ? null : MARK_BY_STATE[previewed]}
        label={CAPABILITY_LABELS[capability]}
        // The floor is always on.
        markReadOnly={CAPABILITY_STOPS[capability] === 0}
        isDisabled={isDisabled}
        onMark={handleMark}
        trailing={
          coverage.state === 'some' ? (
            <span className='shrink-0 text-xs text-fg-tertiary'>
              {describePartialCoverage(coverage.onLabels, coverage.total)}
            </span>
          ) : undefined
        }
      />
    </li>
  );
}

interface StopRowProps {
  stop: AutonomyStop;
  coverage: Record<CapabilityKey, CapabilityCoverage>;
  preview: Record<CapabilityKey, CapabilityCoverage> | null;
  isDisabled: boolean;
  onLevel(stop: AutonomyStop): void;
  onCapability(key: CapabilityKey, on: boolean): void;
}

/** One stop: mark · name – summary · hint/count · chevron, expanding to its
 *  capabilities. */
function StopRow({
  stop,
  coverage,
  preview,
  isDisabled,
  onLevel,
  onCapability,
}: StopRowProps) {
  const current = rollUp(stop, coverage);
  // A stepper preview ghosts the mark; the count and text stay put.
  const previewedRoll = preview ? rollUp(stop, preview) : null;
  const previewed =
    previewedRoll !== null && previewedRoll.state !== current.state
      ? previewedRoll.state
      : null;
  const hint = GROUP_HINTS[stop];
  const keys = CAPABILITY_KEYS.filter(
    key => CAPABILITY_STOPS[key] === stop && coverage[key].state !== 'n/a'
  );
  const target = levelAfterPress(stop, current.state === 'all');
  const handleMark = useCallback(() => onLevel(target), [onLevel, target]);
  if (keys.length === 0) {
    return null;
  }
  return (
    <li
      data-stop={stop}
      data-state={current.state}
      data-preview={previewed ?? undefined}
    >
      <DisclosureRow
        mark={MARK_BY_STATE[current.state]}
        markPreview={previewed === null ? null : MARK_BY_STATE[previewed]}
        markLabel={STOP_LABELS[stop]}
        markReadOnly={stop === 0}
        isDisabled={isDisabled}
        onMark={handleMark}
        title={STOP_LABELS[stop]}
        summary={STOP_SUMMARIES[stop]}
        trailing={
          <>
            {current.state === 'some' && (
              <span className='shrink-0 text-xs text-fg-tertiary'>
                {`${current.onCount} of ${current.total}`}
              </span>
            )}
            {/* Hidden at phone width: a hint beside a wrapping summary steals
                a third of the row. The panel still carries the full story. */}
            {hint && (
              <span className='hidden shrink-0 text-xs text-fg-quaternary sm:inline'>
                {hint}
              </span>
            )}
          </>
        }
      >
        <ul>
          {keys.map(key => (
            <CapabilityRow
              key={key}
              capability={key}
              coverage={coverage[key]}
              preview={preview ? preview[key] : null}
              isDisabled={isDisabled}
              onCapability={onCapability}
            />
          ))}
        </ul>
      </DisclosureRow>
    </li>
  );
}

function AlwaysAsksRow() {
  return (
    <li>
      <AccordionItem>
        <div className='flex items-start gap-3'>
          {/* Same column as the marks: a 24px holder, 4px down. */}
          <span className='-mx-1 mt-1 flex size-6 shrink-0 items-center justify-center'>
            <SlashCircle01
              aria-hidden='true'
              className='size-4 text-fg-quaternary'
            />
          </span>
          <AccordionTrigger className='-mr-2 -ml-1 flex min-w-0 flex-1 items-start gap-3 rounded-md py-1.5 pr-2 pl-1 text-left hover:bg-secondary'>
            <span className='min-w-0 flex-1 text-sm leading-5 text-fg-tertiary'>
              <span className='font-medium text-fg-secondary'>
                {ALWAYS_ASKS_ROW_LABEL}
              </span>
              {' – '}
              {ALWAYS_ASKS_ROW_SUMMARY}
              <RowChevron />
            </span>
          </AccordionTrigger>
        </div>
        <AccordionPanel
          className={`pb-2 text-sm text-fg-tertiary ${ROW_INDENT}`}
        >
          <p>{ALWAYS_ASKS_INTRO}</p>
          <ul className='mt-1 list-disc space-y-0.5 pl-5'>
            {ALWAYS_ASKS_ITEMS.map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className='mt-1'>{ALWAYS_ASKS_FOOTER}</p>
        </AccordionPanel>
      </AccordionItem>
    </li>
  );
}

export interface CapabilityLadderProps {
  /** The grants the ladder describes (the stored ones, or a pending press). */
  grants: AutonomyGrantState;
  /** The grants a hovered stop would produce, or null when not previewing. */
  previewGrants: AutonomyGrantState | null;
  caseTypes: AgentCaseType[];
  isDisabled: boolean;
  /** A stop's mark was pressed: the level it asks for. */
  onLevel(stop: AutonomyStop): void;
  /** A capability's mark was pressed. */
  onCapability(key: CapabilityKey, on: boolean): void;
}

export function CapabilityLadder({
  grants,
  previewGrants,
  caseTypes,
  isDisabled,
  onLevel,
  onCapability,
}: CapabilityLadderProps) {
  const coverage = useMemo(
    () => deriveCapabilityCoverage(grants, caseTypes, CONTACTS_SCOPE_LABEL),
    [grants, caseTypes]
  );
  const preview = useMemo(
    () =>
      previewGrants
        ? deriveCapabilityCoverage(
            previewGrants,
            caseTypes,
            CONTACTS_SCOPE_LABEL
          )
        : null,
    [previewGrants, caseTypes]
  );

  return (
    <div data-component='capability-ladder'>
      <ul className='space-y-0.5'>
        {AUTONOMY_STOPS.map(stop => (
          <StopRow
            key={stop}
            stop={stop}
            coverage={coverage}
            preview={preview}
            isDisabled={isDisabled}
            onLevel={onLevel}
            onCapability={onCapability}
          />
        ))}
        <AlwaysAsksRow />
      </ul>
    </div>
  );
}
